import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  Box,
  Button,
  IconButton,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import {
  FileDownloadOutlined as ExportIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Legend,
  Tooltip,
  Title,
} from "chart.js";
import { Chart } from "react-chartjs-2";
import ChartDataLabels from "chartjs-plugin-datalabels";
import dayjs from "dayjs";
import { Workbook } from "exceljs";
import { saveAs } from "file-saver";
import { toast } from "react-hot-toast";
import { PaginatedTable } from "../../../components";
import { baseAxios, Context } from "../../../utils/config";
import { colors } from "../..";
import {
  chartGrid,
  chartInk,
  chartLabel,
  useThemeMode,
} from "../../../contexts/theme-mode";
import { ClientAppointmentsDetail } from "./client-appointments-detail";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Legend,
  Tooltip,
  Title,
  ChartDataLabels
);

const MAX_RANGE_DAYS = 366;
const dateFieldSx = {
  minWidth: 160,
  "& .MuiOutlinedInput-root": {
    backgroundColor: "background.paper",
    borderRadius: "6px",
  },
};

function formatDateToISO(date) {
  if (!date || !dayjs(date).isValid()) return "";
  return dayjs(date).format("YYYY-MM-DD");
}

function formatMoney(value) {
  const n = Number(value);
  if (Number.isNaN(n)) return "$0.00";
  return `$${n.toFixed(2)}`;
}

function formatMoneyChart(value) {
  const n = Number(value);
  if (Number.isNaN(n)) return "$0";
  if (Math.abs(n - Math.round(n)) < 0.005) return `$${Math.round(n)}`;
  return `$${n.toFixed(2)}`;
}

function formatPercent(value) {
  const n = Number(value);
  if (Number.isNaN(n) || value == null) return "0.00%";
  return `${n.toFixed(2)}%`;
}

function formatShortDate(value) {
  if (!value) return "—";
  const d = dayjs(value);
  return d.isValid() ? d.format("M/D/YY") : "—";
}

function textCell(value) {
  return (
    <Typography
      component="span"
      fontSize={12}
      sx={{ overflowWrap: "break-word", lineHeight: 1.3 }}
    >
      {value || "—"}
    </Typography>
  );
}

function validateDateRange(startDate, endDate) {
  if (
    !startDate ||
    !endDate ||
    !dayjs(startDate).isValid() ||
    !dayjs(endDate).isValid()
  ) {
    toast.error("Please select both start and end dates", { duration: 3000 });
    return false;
  }
  if (dayjs(startDate).isAfter(dayjs(endDate), "day")) {
    toast.error("Start date must be before or equal to end date", {
      duration: 3000,
    });
    return false;
  }
  const daysDiff = dayjs(endDate).diff(dayjs(startDate), "day");
  if (daysDiff > MAX_RANGE_DAYS) {
    toast.error(`Date range cannot exceed ${MAX_RANGE_DAYS} days`, {
      duration: 3000,
    });
    return false;
  }
  return true;
}

function compareRows(a, b, field) {
  const va = a[field];
  const vb = b[field];
  if (typeof va === "boolean" || typeof vb === "boolean") {
    return Number(!!va) - Number(!!vb);
  }
  if (typeof va === "number" || typeof vb === "number") {
    return (Number(va) || 0) - (Number(vb) || 0);
  }
  return String(va ?? "").localeCompare(String(vb ?? ""), undefined, {
    sensitivity: "base",
  });
}

function mapApiError(error, fallback) {
  if (error.response?.status === 403) {
    return "Access denied. You don't have permission to view this report.";
  }
  if (error.response?.status === 404) {
    return "Retention endpoint not found";
  }
  return error.response?.data?.message || fallback;
}

/**
 * Clients Retention Report — stylist / client retention analysis
 */
export default function ClientsRetentionReport() {
  const { location: globalLocation } = useContext(Context);
  const { mode } = useThemeMode();

  const [activeTab, setActiveTab] = useState(0);
  const [startDate, setStartDate] = useState(dayjs().subtract(30, "day"));
  const [endDate, setEndDate] = useState(dayjs());
  const [stylistRows, setStylistRows] = useState([]);
  const [clientRows, setClientRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasRun, setHasRun] = useState(false);
  const [sortBy, setSortBy] = useState("repeatRate");
  const [sortOrder, setSortOrder] = useState("desc");

  const loadReport = useCallback(async () => {
    if (!validateDateRange(startDate, endDate)) return;

    setLoading(true);
    setHasRun(true);
    const params = {
      startDate: formatDateToISO(startDate),
      endDate: formatDateToISO(endDate),
    };

    try {
      if (activeTab === 0) {
        const result = await baseAxios.get("/retention/by-stylist", { params });
        const data = Array.isArray(result.data?.data) ? result.data.data : [];
        setStylistRows(data);
      } else {
        const result = await baseAxios.get("/retention/by-client", { params });
        const data = Array.isArray(result.data?.data) ? result.data.data : [];
        setClientRows(data);
      }
    } catch (error) {
      if (activeTab === 0) setStylistRows([]);
      else setClientRows([]);
      toast.error(
        mapApiError(
          error,
          activeTab === 0
            ? "Error loading stylist retention data"
            : "Error loading client retention data"
        ),
        { duration: 3000 }
      );
    } finally {
      setLoading(false);
    }
  }, [activeTab, endDate, startDate, globalLocation?.location_id]);

  useEffect(() => {
    loadReport();
  }, [loadReport]);

  const handleTabChange = (_event, newValue) => {
    setActiveTab(newValue);
    if (newValue === 0) {
      setSortBy("repeatRate");
      setSortOrder("desc");
    } else {
      setSortBy("daysSinceLastVisit");
      setSortOrder("desc");
    }
  };

  const activeRows = activeTab === 0 ? stylistRows : clientRows;

  const sortedRows = useMemo(() => {
    const copy = [...activeRows];
    copy.sort((a, b) => {
      const c = compareRows(a, b, sortBy);
      return sortOrder === "asc" ? c : -c;
    });
    return copy;
  }, [activeRows, sortBy, sortOrder]);

  const handleSortChange = useCallback((field, order) => {
    setSortBy(field);
    setSortOrder(order);
  }, []);

  const noopPagination = useCallback(() => {}, []);

  const chartData = useMemo(() => {
    const labels = stylistRows.map((r) => r.stylistName || "—");
    return {
      labels,
      datasets: [
        {
          type: "bar",
          label: "Repeat Rate (%)",
          data: stylistRows.map((r) => Number(r.repeatRate) || 0),
          backgroundColor: colors.gold,
          yAxisID: "y",
          order: 2,
        },
        {
          type: "bar",
          label: "Total Revenue ($)",
          data: stylistRows.map((r) => Number(r.totalRevenue) || 0),
          backgroundColor: chartInk(mode),
          yAxisID: "y1",
          order: 1,
        },
      ],
    };
  }, [stylistRows, mode]);

  const chartOptions = useMemo(
    () => {
      const labelColor = chartLabel(mode);
      return {
      responsive: true,
      maintainAspectRatio: false,
      color: labelColor,
      interaction: { mode: "index", intersect: false },
      plugins: {
        legend: {
          position: "bottom",
          labels: { boxWidth: 12, font: { weight: "600" }, color: labelColor },
        },
        title: {
          display: true,
          text: "Stylist Repeat Rate & Revenue",
          color: labelColor,
          font: { size: 14, weight: "600" },
          padding: { bottom: 12 },
        },
        tooltip: {
          callbacks: {
            label: (ctx) => {
              const v = ctx.parsed.y;
              if (ctx.dataset.yAxisID === "y") return `Repeat Rate: ${formatPercent(v)}`;
              return `Revenue: ${formatMoneyChart(v)}`;
            },
          },
        },
        datalabels: {
          display: (ctx) => {
            const v = Number(ctx.dataset.data[ctx.dataIndex]) || 0;
            return v > 0;
          },
          color: (ctx) =>
            ctx.dataset.yAxisID === "y1"
              ? "#ffffff"
              : mode === "dark"
                ? "#1a1a1a"
                : colors.black,
          font: { weight: "600", size: 10 },
          anchor: "end",
          align: "start",
          formatter: (value, ctx) =>
            ctx.dataset.yAxisID === "y"
              ? formatPercent(value)
              : formatMoneyChart(value),
        },
      },
      scales: {
        x: {
          ticks: {
            maxRotation: 45,
            minRotation: 0,
            font: { size: 11 },
            color: labelColor,
          },
          grid: { display: false },
        },
        y: {
          type: "linear",
          position: "left",
          title: { display: true, text: "Repeat Rate (%)", color: labelColor },
          ticks: { color: labelColor },
          beginAtZero: true,
          grid: { color: chartGrid(mode) },
        },
        y1: {
          type: "linear",
          position: "right",
          title: { display: true, text: "Total Revenue ($)", color: labelColor },
          ticks: { color: labelColor },
          beginAtZero: true,
          grid: { drawOnChartArea: false },
        },
      },
    };
    },
    [mode]
  );

  const handleExport = useCallback(async () => {
    if (!sortedRows.length) {
      toast.error("No data to export", { duration: 2500 });
      return;
    }

    try {
      const workbook = new Workbook();
      if (activeTab === 0) {
        const worksheet = workbook.addWorksheet("By Stylist");
        worksheet.columns = [
          { header: "Stylist", key: "stylistName", width: 22 },
          { header: "Total Clients", key: "totalClientsServed", width: 14 },
          { header: "Repeat Clients", key: "repeatClients", width: 14 },
          { header: "Repeat Rate (%)", key: "repeatRate", width: 14 },
          { header: "New Clients", key: "newClients", width: 12 },
          { header: "Returning Clients", key: "returningClients", width: 16 },
          { header: "Total Appointments", key: "totalAppointments", width: 16 },
          { header: "Completed", key: "completedAppointments", width: 12 },
          { header: "Canceled", key: "canceledAppointments", width: 12 },
          { header: "No-Show", key: "noShowAppointments", width: 12 },
          { header: "Cancel Rate (%)", key: "cancellationRate", width: 14 },
          { header: "No-Show Rate (%)", key: "noShowRate", width: 14 },
          {
            header: "Avg Days Between Visits",
            key: "averageDaysBetweenVisits",
            width: 20,
          },
          { header: "Total Revenue", key: "totalRevenue", width: 14 },
        ];
        worksheet.getRow(1).font = { bold: true };
        sortedRows.forEach((row) => worksheet.addRow(row));
      } else {
        const worksheet = workbook.addWorksheet("By Client");
        worksheet.columns = [
          { header: "Client", key: "clientName", width: 22 },
          { header: "Total Appointments", key: "totalAppointments", width: 16 },
          { header: "Completed", key: "completedAppointments", width: 12 },
          { header: "Canceled", key: "canceledAppointments", width: 12 },
          { header: "No-Show", key: "noShowAppointments", width: 12 },
          { header: "Primary Stylist", key: "primaryStylistName", width: 20 },
          { header: "First Visit", key: "firstVisitDate", width: 12 },
          { header: "Last Visit", key: "lastVisitDate", width: 12 },
          { header: "Visit Frequency (days)", key: "visitFrequency", width: 18 },
          { header: "Days Since Last Visit", key: "daysSinceLastVisit", width: 18 },
          { header: "At Risk", key: "isAtRisk", width: 10 },
        ];
        worksheet.getRow(1).font = { bold: true };
        sortedRows.forEach((row) =>
          worksheet.addRow({
            ...row,
            isAtRisk: row.isAtRisk ? "Yes" : "No",
            firstVisitDate: formatShortDate(row.firstVisitDate),
            lastVisitDate: formatShortDate(row.lastVisitDate),
          })
        );
      }

      const buffer = await workbook.xlsx.writeBuffer();
      const dateStr = dayjs().format("YYYY-MM-DD");
      const tabName = activeTab === 0 ? "ByStylist" : "ByClient";
      saveAs(
        new Blob([buffer], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        }),
        `ClientsRetention_${tabName}_${dateStr}.xlsx`
      );
    } catch {
      toast.error("Export failed", { duration: 3000 });
    }
  }, [activeTab, sortedRows]);

  const stylistColumns = useMemo(
    () => [
      {
        id: "stylistName",
        label: "Stylist",
        field: "stylistName",
        width: "10%",
        render: (v) => textCell(v),
      },
      {
        id: "totalClientsServed",
        label: "Total Clients",
        field: "totalClientsServed",
        width: "7%",
        render: (v) => Number(v) || 0,
      },
      {
        id: "repeatClients",
        label: "Repeat Clients",
        field: "repeatClients",
        width: "7%",
        render: (v) => Number(v) || 0,
      },
      {
        id: "repeatRate",
        label: "Repeat Rate (%)",
        field: "repeatRate",
        width: "7%",
        render: (v) => formatPercent(v),
      },
      {
        id: "newClients",
        label: "New Clients",
        field: "newClients",
        width: "6%",
        render: (v) => Number(v) || 0,
      },
      {
        id: "returningClients",
        label: "Returning Clients",
        field: "returningClients",
        width: "7%",
        render: (v) => Number(v) || 0,
      },
      {
        id: "totalAppointments",
        label: "Total Appointments",
        field: "totalAppointments",
        width: "7%",
        render: (v) => Number(v) || 0,
      },
      {
        id: "completedAppointments",
        label: "Completed",
        field: "completedAppointments",
        width: "6%",
        render: (v) => Number(v) || 0,
      },
      {
        id: "canceledAppointments",
        label: "Canceled",
        field: "canceledAppointments",
        width: "6%",
        render: (v) => Number(v) || 0,
      },
      {
        id: "noShowAppointments",
        label: "No-Show",
        field: "noShowAppointments",
        width: "6%",
        render: (v) => Number(v) || 0,
      },
      {
        id: "cancellationRate",
        label: "Cancel Rate (%)",
        field: "cancellationRate",
        width: "7%",
        render: (v) => formatPercent(v),
      },
      {
        id: "noShowRate",
        label: "No-Show Rate (%)",
        field: "noShowRate",
        width: "7%",
        render: (v) => formatPercent(v),
      },
      {
        id: "averageDaysBetweenVisits",
        label: "Avg Days Between Visits",
        field: "averageDaysBetweenVisits",
        width: "8%",
        render: (v) =>
          v == null || Number.isNaN(Number(v)) ? "—" : Number(v).toFixed(1),
      },
      {
        id: "totalRevenue",
        label: "Total Revenue",
        field: "totalRevenue",
        width: "7%",
        align: "right",
        render: (v) => formatMoney(v),
      },
    ],
    []
  );

  const clientColumns = useMemo(
    () => [
      {
        id: "clientName",
        label: "Client",
        field: "clientName",
        width: "12%",
        render: (v) => textCell(v),
      },
      {
        id: "totalAppointments",
        label: "Total Appointments",
        field: "totalAppointments",
        width: "9%",
        render: (v) => Number(v) || 0,
      },
      {
        id: "completedAppointments",
        label: "Completed",
        field: "completedAppointments",
        width: "7%",
        render: (v) => Number(v) || 0,
      },
      {
        id: "canceledAppointments",
        label: "Canceled",
        field: "canceledAppointments",
        width: "7%",
        render: (v) => Number(v) || 0,
      },
      {
        id: "noShowAppointments",
        label: "No-Show",
        field: "noShowAppointments",
        width: "7%",
        render: (v) => Number(v) || 0,
      },
      {
        id: "primaryStylistName",
        label: "Primary Stylist",
        field: "primaryStylistName",
        width: "11%",
        render: (v) => textCell(v),
      },
      {
        id: "firstVisitDate",
        label: "First Visit",
        field: "firstVisitDate",
        width: "8%",
        render: (v) => textCell(formatShortDate(v)),
      },
      {
        id: "lastVisitDate",
        label: "Last Visit",
        field: "lastVisitDate",
        width: "8%",
        render: (v) => textCell(formatShortDate(v)),
      },
      {
        id: "visitFrequency",
        label: "Visit Frequency (days)",
        field: "visitFrequency",
        width: "10%",
        render: (v) =>
          v == null || Number.isNaN(Number(v)) ? "—" : Number(v).toFixed(1),
      },
      {
        id: "daysSinceLastVisit",
        label: "Days Since Last Visit",
        field: "daysSinceLastVisit",
        width: "10%",
        render: (v) => (v == null ? "—" : Number(v) || 0),
      },
      {
        id: "isAtRisk",
        label: "At Risk",
        field: "isAtRisk",
        width: "7%",
        render: (v) => (
          <Typography
            component="span"
            fontSize={12}
            fontWeight={700}
            sx={{ color: v ? "error.main" : "success.main" }}
          >
            {v ? "Yes" : "No"}
          </Typography>
        ),
      },
    ],
    []
  );

  const renderDetail = useCallback(
    (row) => <ClientAppointmentsDetail data={row} />,
    []
  );

  const searchKey = `${activeTab}|${formatDateToISO(startDate)}|${formatDateToISO(endDate)}|${globalLocation?.location_id ?? "all"}`;

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ px: 8, py: 3, width: "100%", minWidth: 0, overflowX: "hidden" }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 2,
            mt: 1.5,
            mb: 2,
          }}
        >
          <Box>
            <Typography fontWeight={600} sx={{ fontSize: "1.5rem" }}>
              Clients Retention Report
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Analyze client retention patterns, repeat booking rates, and
              identify at-risk clients.
            </Typography>
          </Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              flexWrap: "wrap",
            }}
          >
            <DatePicker
              label="Start date"
              value={startDate}
              onChange={(value) => setStartDate(value)}
              slotProps={{ textField: { size: "small", sx: dateFieldSx } }}
            />
            <DatePicker
              label="End date"
              value={endDate}
              onChange={(value) => setEndDate(value)}
              maxDate={dayjs()}
              slotProps={{ textField: { size: "small", sx: dateFieldSx } }}
            />
            <IconButton
              aria-label="Refresh report"
              onClick={loadReport}
              disabled={loading}
              size="medium"
              sx={{
                border: "1px solid",
                borderColor: "divider",
                bgcolor: "background.paper",
                borderRadius: "6px",
                "&:hover": {
                  bgcolor: (theme) =>
                    theme.palette.mode === "dark"
                      ? "rgba(255,255,255,0.08)"
                      : "grey.100",
                },
              }}
            >
              <RefreshIcon />
            </IconButton>
            <Button
              variant="outlined"
              color="secondary"
              startIcon={<ExportIcon />}
              onClick={handleExport}
              disabled={loading || !sortedRows.length}
              sx={{
                minHeight: 40,
                textTransform: "none",
                fontWeight: 600,
              }}
            >
              Export
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={loadReport}
              disabled={loading}
              sx={{
                minHeight: 40,
                px: 2,
                textTransform: "none",
                fontWeight: 700,
                boxShadow: "none",
                "&:hover": { boxShadow: "none" },
              }}
            >
              {loading ? "Loading…" : "Run Report"}
            </Button>
          </Box>
        </Box>

        <Box
          sx={{
            borderBottom: "1px solid",
            borderColor: "divider",
            mb: 2.5,
          }}
        >
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            sx={{
              minHeight: 40,
              "& .MuiTab-root": {
                textTransform: "none",
                fontWeight: 600,
                minHeight: 40,
              },
              "& .Mui-selected": { color: "primary.main" },
              "& .MuiTabs-indicator": { bgcolor: "primary.main" },
            }}
          >
            <Tab label="By Stylist" />
            <Tab label="By Client" />
          </Tabs>
        </Box>

        {activeTab === 0 && stylistRows.length > 0 && (
          <Box
            sx={{
              mb: 2.5,
              p: 2,
              bgcolor: (theme) =>
                theme.palette.mode === "dark" ? "#1b1b1e" : "#f2f2f2",
              border: "2px solid",
              borderColor: (theme) =>
                theme.palette.mode === "dark" ? "#2c2c30" : "secondary.main",
              borderRadius: "6px",
              height: 340,
            }}
          >
            <Chart type="bar" data={chartData} options={chartOptions} />
          </Box>
        )}

        <PaginatedTable
          key={activeTab === 0 ? "stylist-table" : "client-table"}
          columns={activeTab === 0 ? stylistColumns : clientColumns}
          rows={sortedRows}
          totalRowCount={sortedRows.length}
          loading={loading}
          onPaginationChange={noopPagination}
          onSortChange={handleSortChange}
          initialOrderBy={activeTab === 0 ? "repeatRate" : "daysSinceLastVisit"}
          initialOrder="desc"
          searchQuery={searchKey}
          clientSidePagination
          skeletonHeight="40px"
          getRowKey={(row, index) =>
            activeTab === 0
              ? row.stylistId ?? `stylist-${index}`
              : row.clientId ?? `client-${index}`
          }
          renderDetail={activeTab === 1 ? renderDetail : undefined}
          fitWidth
          emptyMessage={
            hasRun
              ? "No retention data for this date range"
              : "Click Run Report to load data"
          }
        />
      </Box>
    </LocalizationProvider>
  );
}
